<script setup lang="ts">
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import Fieldset from 'primevue/fieldset';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import LogYardContent, { type LogYardProductContentModel } from './LogYardContent.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, ref } from 'vue';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { isLogYard } from 'shared/storageAreas/domain/storageAreaType';
import { useMoveApi, type MoveLogYardContent, type MoveRequest as MoveRequest } from '@/moves/api/moveApi';
import { Assert } from 'shared/assert';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useProductStore } from 'shared/products/stores/productStore';
import { estimateVolume } from 'shared/products/domain/product';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const storageAreaStore = useStorageAreaStore();
const productStore = useProductStore();
const moveApi = useMoveApi();
const actionFailedAlert = useActionFailedAlert();

defineExpose({
  show
});

const visible = ref(false);
const loading = ref(false);
const empty = ref(false);

const fromStorageAreaId = ref<string>();
const fromContent = ref<LogYardProductContentModel[]>([]);

const toStorageAreaId = ref<string>();
const toContent = ref<LogYardProductContentModel[]>([]);

const moveContent = ref<MoveContent[]>([]);

const moveRequest = computed(() => {
    const fromStorageAreaIdValue = fromStorageAreaId.value;
    if (!fromStorageAreaIdValue) return;

    const toStorageAreaIdValue = toStorageAreaId.value;
    if (!toStorageAreaIdValue) return;

    if (fromStorageAreaIdValue === toStorageAreaIdValue) return;

    const contents: MoveLogYardContent[] = [];
    let moveCount = 0;

    for (const productContent of moveContent.value) {
      const numberOfLogs = productContent.numberOfLogsText;
      if (typeof numberOfLogs !== 'number') continue;
      moveCount += numberOfLogs;
      const product = productStore.getOrThrow(productContent.productId);
      contents.push({
        numberOfLogs: numberOfLogs,
        productId: productContent.productId,
        volume: estimateVolume(product, numberOfLogs)
      });
    }

    if (moveCount === 0) return;

    const request: MoveRequest = {
      fromStorageAreaId: fromStorageAreaIdValue,
      toStorageAreaId: toStorageAreaIdValue,
      logYardContents: contents,
      empty: empty.value,
    };

    return request;
});

const moveDisabled = computed(() => moveRequest.value ? loading.value : true);

const storageAreas = computed(() => storageAreaStore.storageAreas
  .filter(s => isLogYard(s.type))
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
);

function createMoveContent(content: LogYardProductContentModel[]) : MoveContent[] {

  const result: MoveContent[] = [];

  for (const productContent of content) {
    result.push({
      productId: productContent.productId,
      productName: productContent.productName,
      numberOfLogs: 0,
      numberOfLogsText: ''
    });
  }

  return result;
}

function createStorageAreaContent(storageAreaId: string | undefined) : LogYardProductContentModel[] {
  const result: LogYardProductContentModel[] = [];
  if (!storageAreaId) return result;
  const storageArea = storageAreaStore.getById(storageAreaId);
  if (!storageArea || !storageArea.logYardData) return result;

  for (const content of storageArea.logYardData.content) {
    const product = productStore.getById(content.productId);
    if (!product) continue;
    result.push({
      numberOfLogs: content.numberOfLogs,
      productId: product.id,
      productName: product.name,
      volume: content.volume
    });
  }

  result.sort((a, b) => a.productName.localeCompare(b.productName, undefined, { numeric: true }));

  return result;
}

interface MoveContent {
  productId: string;
  productName: string;
  numberOfLogs: number;
  numberOfLogsText: string|number|undefined;
}

interface ShowMoveLogYardContentDialog {
  fromStorageAreaId: string;
  toStorageAreaId: string;
}

function show(action: ShowMoveLogYardContentDialog) {
  reset();
  selectFromStorageArea(action.fromStorageAreaId);
  selectToStorageArea(action.toStorageAreaId);
  visible.value = true;
}

function reset() {
  visible.value = false;
  loading.value = false;
  fromStorageAreaId.value = undefined;
  toStorageAreaId.value = undefined;
}

function onSwapStorageAreas() {
  const newToStorageAreaId = fromStorageAreaId.value;
  const newFromStorageAreaId = toStorageAreaId.value;
  selectFromStorageArea(newFromStorageAreaId);
  selectToStorageArea(newToStorageAreaId);
}

function selectFromStorageArea(id: string|undefined) {
  fromStorageAreaId.value = id;
  fromContent.value = createStorageAreaContent(id);
  moveContent.value = createMoveContent(fromContent.value);
}

function selectToStorageArea(id: string|undefined) {
  toStorageAreaId.value = id;
  toContent.value = createStorageAreaContent(id);
}

async function onMoveClick() {
  try {
    loading.value = true;
    const request = moveRequest.value;
    Assert.definied(request);

    const response = await moveApi.execute(request);

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    const move = response.move;

    storageAreaStore.add(move.fromStorageArea);
    storageAreaStore.add(move.toStorageArea);

    show({
      fromStorageAreaId: move.fromStorageArea.id,
      toStorageAreaId: move.toStorageArea.id
    });

  } finally {
    loading.value = false;
  }
}

function onLogYardContentClick(content: LogYardProductContentModel) {
  for (const productContent of moveContent.value) {
    if (productContent.productId !== content.productId) continue;
    productContent.numberOfLogsText = content.numberOfLogs;
    productContent.numberOfLogs = content.numberOfLogs;
  }
}

function hasChanged(content: MoveContent) {
  return typeof content.numberOfLogsText === "number" && content.numberOfLogsText > 0;
}

</script>

<template>
  <Dialog :header="translations.logYard.moveDialog.title" v-model:visible="visible">
    <div class="move-log-yard-content-grid">
      <Fieldset :legend="translations.generic.from">
        <div class="storage-area-field">
          <label>{{ translations.generic.storageArea }}</label>
          <Select autocomplete="off" v-model:modelValue="fromStorageAreaId"
            :options="storageAreas" option-value="id" option-label="name" autofocus @change="(ev) => selectFromStorageArea(ev.value)"></Select>
        </div>

        <LogYardContent :products="fromContent" @click="onLogYardContentClick"></LogYardContent>

      </Fieldset>
      <Fieldset :legend="translations.generic.move">
        <div v-for="content in moveContent" v-bind:key="content.productId" class="field"
          :class="{ changed: hasChanged(content) }">
          <label>{{ content.productName }}</label>
          <InputNumber v-model="content.numberOfLogs" @input="(e) => content.numberOfLogsText = e.value"></InputNumber>
        </div>
        <div class="pick-all-area">
          <Checkbox v-model="empty" inputId="pickAll" :binary="true" />
          <label for="pickAll">{{ translations.dialogs.moveLogYardContent.pickAll }}</label>
        </div>
      </Fieldset>
      <Fieldset :legend="translations.generic.to">
        <div class="storage-area-field">
          <label>{{ translations.generic.storageArea }}</label>
          <Select autocomplete="off" v-model:modelValue="toStorageAreaId"
            :options="storageAreas" option-value="id" option-label="name" autofocus @change="(ev) => selectToStorageArea(ev.value)"></Select>
        </div>

        <LogYardContent :products="toContent"></LogYardContent>
      </Fieldset>
    </div>
    <template #footer>
      <Button
        severity="secondary"
        :label="translations.generic.swap" @click="onSwapStorageAreas">
      </Button>
      <Button :disabled="moveDisabled"
        :label="translations.generic.move" @click="onMoveClick" :loading="loading"></Button>
    </template>
  </Dialog>
</template>

<style scoped>

.move-log-yard-content-grid {
  display: grid;
  grid-template-rows: auto auto auto;
  row-gap: 1rem;
}

.storage-area-field {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: .5rem;
  padding-bottom: .5rem;
}

.pick-all-area {
  margin-top: .5rem;
  display: flex;
  column-gap: 1rem;
  justify-content: left;
  align-items: center;
  padding-block: 0.5rem;
}

</style>
